import { useState, useCallback } from "react";
import { generateAndDownloadFile, downloadBase64Png } from "utils/functions";
import getModuleName from "utils/getModuleName";
import { buildPreparedTablesCsvString } from "utils/buildPreparedTablesCsvString";

import Button from "components/Button";
import Loader from "components/Loader";
import "./download-section.css";

const JPG_LAYOUT_REF_WIDTH = 1200;

const canvasFont = (style, sizePx) =>
   `${style} ${sizePx}px "Outfit",sans-serif`;

async function ensureOutfitForCanvas() {
   if (typeof document === "undefined" || !document.fonts) return;
   await document.fonts.ready;
   try {
      await document.fonts.load("19px Outfit");
   } catch {
      /* brak wsparcia lub sieć — fallback sans-serif */
   }
}

export default function DownloadSection({
   chart_footer,
   mode,
   tables,
   source,
   getDivJpeg,
   udZipExport = null
}) {
   const [loading, setLoading] = useState(false);

   const handleDivDownload = useCallback(async () => {
      await ensureOutfitForCanvas();

      const jpeg = await getDivJpeg();
      if (!jpeg) return;

      const img = new Image();
      const loaded = new Promise((resolve, reject) => {
         img.onload = () => resolve();
         img.onerror = () => reject(new Error("Nie udało się wczytać obrazu wykresu."));
      });
      img.src = jpeg;
      await loaded;

      const bitmapW = img.naturalWidth || img.width;
      const bitmapH = img.naturalHeight || img.height;
      const scale = bitmapW / JPG_LAYOUT_REF_WIDTH;

      const footerFontSize = Math.max(12, Math.round(19 * scale));
      const extraBottom = chart_footer ? Math.max(40, Math.round(57 * scale)) : 0;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = bitmapW;
      canvas.height = bitmapH + extraBottom;

      ctx.drawImage(img, 0, 0);

      if (chart_footer) {
         ctx.font = canvasFont("normal", footerFontSize);
         ctx.fillStyle = "white";
         ctx.fillRect(0, bitmapH, canvas.width, extraBottom);
         ctx.fillStyle = "black";

         ctx.textAlign = "center";
         ctx.textBaseline = "middle";
         ctx.fillText(
            chart_footer,
            canvas.width / 2,
            bitmapH + extraBottom / 2
         );

         ctx.textAlign = "start";
         ctx.textBaseline = "alphabetic";
      }

      const final = canvas.toDataURL("image/jpeg");
      downloadBase64Png(final, "wykres_zskstat.jpeg");
   }, [chart_footer, getDivJpeg]);

   const generateCSVFile = (tables) => {
      const module = getModuleName();
      const data_str = buildPreparedTablesCsvString(tables, module);
      generateAndDownloadFile(data_str, "tabela_zskstat.csv", "text/csv;charset=utf-8;");
   };

   const onDownload = async () => {
      setLoading(true);

      if (mode === "table") {
         if (udZipExport?.active && typeof udZipExport.exportZip === "function") {
            try {
               await udZipExport.exportZip();
            } catch (err) {
               console.error(err);
               alert(
                  err?.message ||
                     "Nie udało się przygotować pliku ZIP. Spróbuj ponownie."
               );
            }
         } else {
            generateCSVFile(tables);
         }
      }
      if (mode === "chart") await handleDivDownload();

      setLoading(false);
   };

   const getDownloadLabel = () => {
      if (mode === "table") {
         if (udZipExport?.active) return "Pobierz (ZIP z CSV)";
         return "Pobierz (CSV, ~50KB)";
      } else if (mode === "chart") {
         return "Pobierz (JPG, ~100KB)";
      }
      return "Pobierz";
   };

   const getDownloadAriaLabel = () => {
      if (mode === "table") {
         if (udZipExport?.active) {
            return "Pobierz archiwum ZIP z osobnym plikiem CSV dla każdego wskaźnika";
         }
         return "Pobierz raport w formacie CSV, rozmiar około 50KB";
      } else if (mode === "chart") {
         return "Pobierz raport w formacie JPG, rozmiar około 100KB";
      }
      return "Pobierz raport";
   };

   return !loading ? (
      <>
         <div className="download-section">
            <span>Udostępnij wyniki:</span>
            <Button 
               label={getDownloadLabel()} 
               icon="download" 
               ariaLabel={getDownloadAriaLabel()}
               onClick={onDownload} 
            />
            <Button
               label="Udostępnij"
               icon="share"
               extra_classes="white"
               onClick={() => {
                  const item = new ClipboardItem({
                     "text/plain": window.location.href,
                  });
                  navigator.clipboard
                     .write([item])
                     .then(() =>
                        alert(
                           "Link do udostępniania został pomyślnie skopiowany do schowka."
                        )
                     );
               }}
            />
         </div>

         { source && 
            <p dangerouslySetInnerHTML={{__html: "Źródło danych: " +  source }}/>
         }
      </>
   ) : (
      <Loader />
   );
}